import React, { useState, useEffect } from "react";
import { Axios } from "../utils/UtilityFunctions";
import "../css/Login.css";
import clsx from "clsx";
import {
  TextField,
  Grid,
  Container,
  Typography,
  Box,
  Link,
  CssBaseline,
  Button,
  IconButton,
  InputAdornment,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { EmailOutlined } from "@material-ui/icons";

import logo from "../images/flyins-logo.svg";
import VerifyEmail from "./VerifyEmail";

import { validateEmail, validatePassword } from "../utils/UtilityFunctions";
import { useHistory } from "react-router-dom";
import ToastrMsg from "../components/Toaster";
import { useTranslation } from "react-i18next";
import {
  PasswordIcon,
  PasswordActiveIcon,
  ShowPasswordIcon,
  HidePasswordIcon,
} from "../components/CustomIcons";

const useStyles = makeStyles((theme) => ({
  insidePaper: {
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
  },
  main: {
    marginTop: theme.spacing(7),
    fontFamily: "Inter",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    backgroundColor: "#5046B2",
    height: 50,
  },
  paper: {
    display: "flex",
    flexWrap: "wrap",
    "& > *": {
      margin: theme.spacing(1),
      padding: "32px 0",
    },
  },
  input: {
    "& input:-webkit-autofill": {
      "-webkit-box-shadow": "0 0 0 100px #fafafa inset",
      "-webkit-text-fill-color": "inherit",
    },
    marginBottom: 27,
  },
  icon: {
    fontSize: 20,
  },
  grid: {
    textAlign: "right",
  },
  textField: {
    "& fieldset": {
      borderColor: "black",
    },
    "&:hover fieldset": {
      borderColor: "black",
    },
    "& input:-webkit-autofill": {
      "-webkit-box-shadow": "0 0 0 100px #fafafa inset",
      "-webkit-text-fill-color": "inherit",
    },
    "& .MuiIconButton-root.MuiIconButton-edgeEnd:hover": {
      background: "transparent",
    },
    marginBottom: 27,
  },
}));

export default function Login() {
  const classes = useStyles();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [toastrMsg, setToastrMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);

  const history = useHistory();

  const { t, i18n } = useTranslation();

  const handleKeyDown = (event) => {
    if (event.key == "Enter") {
      event.preventDefault();
      submitCreateAccount();
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const submitCreateAccount = async () => {
    setErrorMessage("");
    setToastrMsg("");

    // Validate form fields
    if (!email) {
      setErrorMessage("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setErrorMessage("Please enter a valid email");
      return;
    }

    if (!password) {
      setErrorMessage("Password is required");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Password and confirm password do not match");
      return;
    }

    if (!validatePassword(password)) {
      setErrorMessage(
        "Password must have a minimum of 6 characters, including an uppercase letter, numbers, and special characters."
      );
      return;
    }

    // Prepare payload
    const payload = {
      email,
      password,
      confirmPassword,
    };

    try {
      const response = await Axios("POST", "/auth/create-account", payload);

      // Check if the API response contains an error
      if (response.data.success === false) {
        // Log the specific error message from the response
        console.log("API Error:", response.data.error);
        setErrorMessage(response.data.error || "Error Creating an Account.");
        return response;
      }

      // If successful, show the verification prompt
      setShowVerifyEmail(true);
    } catch (error) {
      // Axios can throw an error for failed requests (like 400 or 500 errors)
      // If an error occurs during the request (e.g., network issues), handle it here
      console.error("Request failed:", error);

      // Check if the error has a response (like in a 400 or 500 HTTP error)
      if (error.response) {
        console.log("Error Response:", error.response.data);
        setErrorMessage(
          error.response.data.error || "Error Creating an Account."
        );
      } else {
        // If the error is not from the response (e.g., network issue), show a fallback message
        setErrorMessage("An unexpected error occurred. Please try again.");
      }
    }
  };

  if (!showVerifyEmail) {
    return (
      <>
        {{ toastrMsg }.toastrMsg && (
          <ToastrMsg type="error" msg={toastrMsg} title="" />
        )}
        <Container
          component="main"
          maxWidth="xs"
          className={clsx(classes.main, "form-style")}
        >
          <CssBaseline />
          <Box
            maxWidth="xs"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <img
              src={logo}
              alt="flyins"
              width="307"
              height="100"
              draggable={false}
            />
          </Box>

          <div className={classes.paper}>
            <Paper elevation={2}>
              <div className={classes.insidePaper}>
                <Typography
                  component="h1"
                  variant="h5"
                  style={{ marginBottom: 47, fontWeight: 500, fontSize: 40 }}
                >
                  {t("Create an account")}
                </Typography>
                <form className={classes.form} noValidate>
                  <Typography style={{ color: "red", marginBottom: 16 }}>
                    {errorMessage}
                  </Typography>
                  <TextField
                    variant="outlined"
                    //   margin="noral"
                    required
                    fullWidth
                    className={email !== "" ? classes.textField : classes.input}
                    // className={classes.input}
                    id="email"
                    label={t("Email Address")}
                    name="email"
                    autoComplete="email"
                    value={email}
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlined
                            className={classes.icon}
                            style={{
                              color: email.length ? "#000000" : "#868e96",
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <TextField
                    variant="outlined"
                    //   margin="normal"
                    required
                    fullWidth
                    style={{ marginBottom: 8 }}
                    className={email !== "" ? classes.textField : classes.input}
                    name="password"
                    label={t("Password")}
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    autoComplete="current-password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {password === "" ? (
                            <PasswordIcon
                              className={classes.icon}
                              style={{ marginTop: 10 }}
                            />
                          ) : (
                            <PasswordActiveIcon
                              className={classes.icon}
                              style={{ marginTop: 10 }}
                            />
                          )}
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleTogglePassword} edge="end">
                            {showPassword ? (
                              <ShowPasswordIcon
                                className={classes.icon}
                                style={{ marginTop: 10 }}
                              />
                            ) : (
                              <HidePasswordIcon
                                className={classes.icon}
                                style={{ marginTop: 10 }}
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <Typography
                    style={{
                      fontSize: "12px",
                      color: "#495057",
                      marginBottom: 27,
                    }}
                  >
                    Minimum of 6 characters. Contains an uppercase letter,
                    numbers and special characters.
                  </Typography>
                  <TextField
                    variant="outlined"
                    //   margin="normal"
                    required
                    fullWidth
                    className={email !== "" ? classes.textField : classes.input}
                    name="confirmPassword"
                    label={t("Confirm Password")}
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirm-password"
                    value={confirmPassword}
                    autoComplete="current-password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {confirmPassword === "" ? (
                            <PasswordIcon
                              className={classes.icon}
                              style={{ marginTop: 10 }}
                            />
                          ) : (
                            <PasswordActiveIcon
                              className={classes.icon}
                              style={{ marginTop: 10 }}
                            />
                          )}
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleToggleConfirmPassword}
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <ShowPasswordIcon
                                className={classes.icon}
                                style={{ marginTop: 10 }}
                              />
                            ) : (
                              <HidePasswordIcon
                                className={classes.icon}
                                style={{ marginTop: 10 }}
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />

                  <Button
                    type="button"
                    fullWidth
                    variant="contained"
                    color="primary"
                    id="submit"
                    className={classes.submit}
                    onClick={submitCreateAccount}
                  >
                    {t("Create Account")}
                  </Button>
                  <Grid container style={{ textAlign: "center" }}>
                    <Grid item xs>
                      {t("Already have an account? ")}
                      <Link href="login" variant="body2">
                        {t("Login")}
                      </Link>
                    </Grid>
                  </Grid>
                </form>
              </div>
            </Paper>
          </div>
        </Container>
      </>
    );
  } else {
    return (
      <VerifyEmail
        userEmail={email}
        resendEmailCallBack={() => submitCreateAccount()}
      />
    );
  }
}
